// Bundled demo data used when Supabase isn't configured (demo mode).
// Mirrors the shapes returned by src/services/db.ts.
// Product IDs are 1-indexed to match supabase/seed.sql (guarded by
// src/data/demoSeedParity.test.ts). Campaigns and chats have no seeded rows —
// they are created by users — so their demo IDs are arbitrary.
import type { Campaign, Chat, Product } from '../types';

export const DEMO_PRODUCTS = [
  {
    id: 1,
    title: 'Cinematic LUT Pack Vol. 1',
    creator: 'Jordan Reeves',
    handle: '@jordanreeves',
    category: 'Presets',
    tags: ['Video', 'Cinematic'],
    emoji: '🎬',
    bg: '#1a1208',
    price: 29,
    sales: 312,
    commission: 30,
  },
  {
    id: 2,
    title: 'Content Creator OS',
    creator: 'Maya Chen',
    handle: '@mayachen.creates',
    category: 'Templates',
    tags: ['Notion', 'Productivity'],
    emoji: '📋',
    bg: '#10101a',
    price: 49,
    sales: 841,
    commission: 25,
  },
  {
    id: 3,
    title: 'Faith-Led Brand Bible',
    creator: 'Caleb St. James',
    handle: '@calebstjames',
    category: 'E-Book',
    tags: ['Branding', 'Faith'],
    emoji: '📖',
    bg: '#0e1410',
    price: 19,
    sales: 204,
    commission: 40,
  },
  {
    id: 4,
    title: 'SaaS Launch Playbook',
    creator: 'Sofia Voss',
    handle: '@sofiavoss',
    category: 'Course',
    tags: ['Tech', 'Strategy'],
    emoji: '🚀',
    bg: '#161208',
    price: 97,
    sales: 128,
    commission: 35,
  },
  {
    id: 5,
    title: 'Wellness Brand Kit',
    creator: 'Priya Nair',
    handle: '@priyanair.co',
    category: 'Templates',
    tags: ['Canva', 'Wellness'],
    emoji: '🌿',
    bg: '#0c1210',
    price: 39,
    sales: 567,
    commission: 30,
  },
  {
    id: 6,
    title: 'Raw Reels Preset Bundle',
    creator: 'Marcus Obi',
    handle: '@marcobi',
    category: 'Presets',
    tags: ['Mobile', 'Raw'],
    emoji: '📱',
    bg: '#180e08',
    price: 24,
    sales: 430,
    commission: 30,
  },
  {
    id: 7,
    title: 'Peaceful Living Guide',
    creator: 'Leila Karimi',
    handle: '@leilakarimi',
    category: 'E-Book',
    tags: ['Lifestyle', 'Faith'],
    emoji: '🌙',
    bg: '#101018',
    price: 15,
    sales: 189,
    commission: 40,
  },
  {
    id: 8,
    title: 'Dev Portfolio Masterclass',
    creator: 'Dev Sharma',
    handle: '@devsharma.dev',
    category: 'Course',
    tags: ['Tech', 'Career'],
    emoji: '⚡',
    bg: '#141008',
    price: 79,
    sales: 93,
    commission: 35,
  },
] satisfies Product[];

export const DEMO_CAMPAIGNS = [
  {
    id: 1,
    title: 'Summer Skincare Launch',
    budget: '$1,200',
    status: 'Active',
    applied: 8,
    selected: 2,
    deadline: 'Apr 25, 2026',
  },
  {
    id: 2,
    title: 'Fitness App Promo',
    budget: '$800',
    status: 'In Progress',
    applied: 5,
    selected: 3,
    deadline: 'Apr 18, 2026',
  },
  {
    id: 3,
    title: 'Faith Lifestyle Series',
    budget: '$500',
    status: 'Active',
    applied: 12,
    selected: 1,
    deadline: 'May 2, 2026',
  },
] satisfies Campaign[];

export const DEMO_CHATS = [
  {
    id: 0,
    name: 'Jordan Reeves',
    init: 'JR',
    campaign: 'Summer Launch · TikTok Video',
    status: 'In Review',
    statusClass: 'status-review',
    unread: true,
    revisions: 1,
    maxRevisions: 3,
    preview: 'Uploaded the final cut...',
    messages: [
      {
        from: 'creator',
        text: 'Hey! I just uploaded the final cut for your review. Really happy with how it turned out.',
        time: '2:14 PM',
      },
      {
        from: 'brand',
        text: 'Love the hook in the first 3 seconds. One ask: can you add a CTA overlay at the end?',
        time: '2:31 PM',
      },
      {
        from: 'creator',
        text: "Absolutely, I'll have that revision ready within the hour.",
        time: '2:33 PM',
      },
      {
        from: 'creator',
        file: 'final_cut_v2.mp4',
        fileType: 'MP4',
        delivery: false,
        time: '3:07 PM',
      },
    ],
  },
  {
    id: 1,
    name: 'Maya Chen',
    init: 'MC',
    campaign: 'Fall Collection · UGC Ad',
    status: 'Active',
    statusClass: 'status-active',
    unread: false,
    revisions: 0,
    maxRevisions: 3,
    preview: 'Can I adjust the deadline?',
    messages: [
      {
        from: 'brand',
        text: 'Hi Maya! Excited for this one. Brief is attached.',
        time: 'Yesterday',
      },
      {
        from: 'creator',
        text: 'Thank you! Can I adjust the deadline by 1 day? Want the lighting to be perfect.',
        time: 'Yesterday',
      },
      { from: 'brand', text: 'No problem — take the time you need.', time: 'Yesterday' },
    ],
  },
  {
    id: 2,
    name: 'Caleb St. James',
    init: 'CS',
    campaign: 'Faith Community · Lifestyle',
    status: 'In Review',
    statusClass: 'status-review',
    unread: false,
    revisions: 2,
    maxRevisions: 3,
    preview: 'Revision 2 is ready',
    messages: [
      {
        from: 'creator',
        text: 'Revision 2 is ready! Incorporated all your feedback from round one.',
        time: '10:15 AM',
      },
      {
        from: 'brand',
        text: 'This looks excellent. Sending for final approval now.',
        time: '11:00 AM',
      },
      {
        from: 'creator',
        file: 'lifestyle_rev2.mp4',
        fileType: 'MP4',
        delivery: true,
        time: '11:04 AM',
      },
    ],
  },
  {
    id: 3,
    name: 'Sofia Voss',
    init: 'SV',
    campaign: 'TechDrop Launch · TikTok',
    status: 'Active',
    statusClass: 'status-active',
    unread: false,
    revisions: 0,
    maxRevisions: 3,
    preview: 'Excited to get started...',
    messages: [
      {
        from: 'brand',
        text: 'Sofia, product sample is on its way. Let us know when it arrives!',
        time: 'Mon',
      },
      { from: 'creator', text: 'Sounds great, excited to get started on this one!', time: 'Mon' },
    ],
  },
] satisfies Chat[];
